import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  TCompanyTopics,
  TCompanyAnalytics,
  TCompanyEvaluations,
  TCompanyTopicGrouping,
  TCompanyEngagementPoint,
} from 'librechat-data-provider';
import { useCompanyAnalyticsQuery } from './hooks';
import {
  Bar,
  Chip,
  Muted,
  Panel,
  Section,
  FilterBar,
  PageHeader,
  PanelHead,
  Segmented,
  IconTile,
  Suppressed,
  LinkAction,
  FilterSelect,
} from './Chrome';
import { formatChange, formatCount, formatPct, type Change } from './format';

type Period = '4w' | '12w' | '26w';

const PERIODS: Period[] = ['4w', '12w', '26w'];

const EVALUATION_LABEL: Record<string, string> = {
  clarity: 'com_ui_leadhr_eval_clarity',
  understood: 'com_ui_leadhr_eval_understood',
  psychologicallySafe: 'com_ui_leadhr_eval_safe',
  applied: 'com_ui_leadhr_eval_applied',
  wouldRecommend: 'com_ui_leadhr_eval_recommend',
  conversationUseful: 'com_ui_leadhr_eval_useful',
};

const FUNNEL_LABEL: Record<string, string> = {
  invited: 'com_ui_leadhr_funnel_invited',
  signedUp: 'com_ui_leadhr_funnel_signed_up',
  activated: 'com_ui_leadhr_funnel_activated',
  active7d: 'com_ui_leadhr_funnel_active7d',
};

const TILE_SAND = 'var(--lh-sand)';
const TILE_GREEN = 'var(--lh-lgreen)';

export default function Analytics({
  groupId,
  onInvite,
  onViewInvitations,
}: {
  groupId: string;
  onInvite: () => void;
  onViewInvitations: () => void;
}): ReactElement | null {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>('12w');
  const [groupBy, setGroupBy] = useState<TCompanyTopicGrouping>('managementLevel');

  const { data, isLoading } = useCompanyAnalyticsQuery(groupId, { period, groupBy });

  if (isLoading && !data) {
    return <Muted>{t('com_ui_leadhr_loading')}</Muted>;
  }
  if (!data) {
    return null;
  }

  return (
    <>
      <PageHeader
        title={t('com_ui_leadhr_analytics_title')}
        subtitle={t('com_ui_leadhr_analytics_subtitle')}
      />

      <FilterBar>
        <FilterSelect
          id="leadhr-period"
          label={t('com_ui_leadhr_filter_period')}
          value={period}
          options={PERIODS.map((value) => ({ value, label: t(`com_ui_leadhr_period_${value}`) }))}
          onChange={(value) => setPeriod(value as Period)}
        />
      </FilterBar>

      <div className="flex flex-col gap-10">
        <Section
          title={t('com_ui_leadhr_glance_title')}
          subtitle={t('com_ui_leadhr_glance_subtitle')}
        >
          <Tiles analytics={data} />
        </Section>

        <Section
          title={t('com_ui_leadhr_usage_title')}
          subtitle={t('com_ui_leadhr_usage_subtitle')}
        >
          <Panel className="flex flex-col gap-5">
            <PanelHead
              title={t('com_ui_leadhr_engagement_title')}
              subtitle={t('com_ui_leadhr_engagement_subtitle')}
            />
            <Engagement points={data.engagement} />
          </Panel>
          <Panel className="flex flex-col gap-4">
            <PanelHead
              title={t('com_ui_leadhr_topics_title')}
              subtitle={t('com_ui_leadhr_topics_subtitle')}
              aside={
                <Segmented<TCompanyTopicGrouping>
                  label={t('com_ui_leadhr_topics_title')}
                  value={groupBy}
                  onChange={setGroupBy}
                  options={[
                    { value: 'managementLevel', label: t('com_ui_leadhr_filter_level') },
                    { value: 'region', label: t('com_ui_leadhr_filter_region') },
                  ]}
                />
              }
            />
            <Topics topics={data.topics} />
          </Panel>
        </Section>

        <Section
          title={t('com_ui_leadhr_evaluations_title')}
          subtitle={
            data.evaluations.available
              ? t('com_ui_leadhr_evaluations_basis', {
                  responses: formatCount(data.evaluations.responses),
                  rate: data.evaluations.responseRatePct,
                })
              : t('com_ui_leadhr_evaluations_subtitle')
          }
        >
          <Evaluations evaluations={data.evaluations} />
        </Section>

        <Section title={t('com_ui_leadhr_look_title')} subtitle={t('com_ui_leadhr_look_subtitle')}>
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,360px),1fr))' }}
          >
            <Funnel analytics={data} />
            <Attention analytics={data} onInvite={onInvite} onViewInvitations={onViewInvitations} />
          </div>
        </Section>
      </div>
    </>
  );
}

function DeltaChip({ change, title }: { change: Change; title: string }): ReactElement {
  return (
    <span title={title}>
      <Chip
        background={change.down ? 'var(--lh-apricot)' : undefined}
        color={change.down ? 'var(--lh-apricot-ink)' : undefined}
      >
        {change.arrow ? <span aria-hidden="true">{change.arrow}</span> : null}
        {change.text}
      </Chip>
    </span>
  );
}

function Tile({
  label,
  background,
  children,
}: {
  label: string;
  background: string;
  children: ReactElement | ReactElement[];
}): ReactElement {
  return (
    <div
      className="flex min-h-[176px] flex-col justify-between rounded-lg p-5"
      style={{ background }}
    >
      <p className="m-0 leading-[1.6]">{label}</p>
      <div>{children}</div>
    </div>
  );
}

function TileNote({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <p className="m-0 mt-1.5 text-xs" style={{ color: 'var(--lh-ink-soft)' }}>
      {children}
    </p>
  );
}

function Tiles({ analytics }: { analytics: TCompanyAnalytics }): ReactElement {
  const { t } = useTranslation();
  const { seats, assigned, usagePct } = analytics.licences;
  const { signedUp, open, total } = analytics.invitations;
  const signedUpPct = total === 0 ? 0 : Math.round((signedUp / total) * 100);
  const vsPrevious = t('com_ui_leadhr_vs_previous');

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))' }}
    >
      <Tile label={t('com_ui_leadhr_licences_assigned')} background={TILE_SAND}>
        <p className="lh-stat m-0">
          {formatCount(assigned)}
          {seats === null ? null : (
            <small
              className="text-sm tracking-normal"
              style={{ font: '400 14px var(--lh-font-ui)' }}
            >
              {' '}
              / {formatCount(seats)}
            </small>
          )}
        </p>
        {usagePct === null || seats === null ? (
          <TileNote>{t('com_ui_leadhr_no_limit_help')}</TileNote>
        ) : (
          <>
            <div className="mt-2.5">
              <Bar width={usagePct} label={t('com_ui_leadhr_in_use', { pct: usagePct })} />
            </div>
            <TileNote>
              {t('com_ui_leadhr_licences_open', { count: Math.max(seats - assigned, 0) })}
            </TileNote>
          </>
        )}
      </Tile>

      <Tile label={t('com_ui_leadhr_invitations_accepted')} background={TILE_GREEN}>
        <p className="lh-stat m-0">
          {formatCount(signedUp)}
          <small className="text-sm tracking-normal" style={{ font: '400 14px var(--lh-font-ui)' }}>
            {' '}
            / {formatCount(total)}
          </small>
        </p>
        <div className="mt-2.5">
          <Bar width={signedUpPct} label={formatPct(signedUpPct)} />
        </div>
        <TileNote>
          {t('com_ui_leadhr_invitations_note', { pct: signedUpPct, n: formatCount(open) })}
        </TileNote>
      </Tile>

      <Tile label={t('com_ui_leadhr_active_users')} background={TILE_SAND}>
        <p className="lh-stat m-0">{formatCount(analytics.activeUsers.current)}</p>
        <div
          className="mt-1.5 flex items-center gap-2 text-xs"
          style={{ color: 'var(--lh-ink-soft)' }}
        >
          <DeltaChip change={formatChange(analytics.activeUsers.changePct)} title={vsPrevious} />
          {t('com_ui_leadhr_active_users_help')}
        </div>
      </Tile>

      <Tile label={t('com_ui_leadhr_conversations')} background={TILE_SAND}>
        <p className="lh-stat m-0">{formatCount(analytics.conversations.current)}</p>
        <div
          className="mt-1.5 flex items-center gap-2 text-xs"
          style={{ color: 'var(--lh-ink-soft)' }}
        >
          <DeltaChip change={formatChange(analytics.conversations.changePct)} title={vsPrevious} />
          {t('com_ui_leadhr_avg_messages', { count: analytics.conversations.avgMessages })}
        </div>
      </Tile>
    </div>
  );
}

const AXIS_STEPS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

/** Rounds the peak up to a scale whose four intervals are whole, readable numbers. */
function axisScale(peak: number): { max: number; ticks: number[] } {
  const raw = Math.max(peak, 1) / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step =
    AXIS_STEPS.map((m) => m * magnitude).find((s) => s >= raw && Number.isInteger(s)) ??
    Math.ceil(raw);
  return { max: step * 4, ticks: [4, 3, 2, 1, 0].map((n) => n * step) };
}

function Engagement({ points }: { points: TCompanyEngagementPoint[] }): ReactElement {
  const { t } = useTranslation();
  if (points.length === 0) {
    return <Muted>{t('com_ui_leadhr_no_engagement')}</Muted>;
  }
  const { max, ticks } = axisScale(Math.max(...points.map((point) => point.conversations)));
  /** Thin the date labels once the weeks no longer fit beside each other. */
  const labelEvery = Math.ceil(points.length / 13);

  return (
    <div className="flex gap-3.5">
      <div
        className="flex h-[200px] flex-none flex-col justify-between text-right text-xs"
        style={{ color: 'var(--lh-muted)' }}
        aria-hidden="true"
      >
        {ticks.map((tick) => (
          <span key={tick}>{formatCount(tick)}</span>
        ))}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div
          className="flex h-[200px] items-end gap-2.5"
          style={{ borderBottom: '1px solid var(--lh-rule)' }}
        >
          {points.map((point) => (
            <div
              key={point.weekStart}
              className="flex h-full min-w-0 flex-1 items-end justify-center"
            >
              <div
                className="w-3/5 rounded-t"
                style={{
                  height: `${(point.conversations / max) * 100}%`,
                  background: 'var(--lh-ink)',
                }}
                title={`${point.conversations}`}
              />
            </div>
          ))}
        </div>
        <div className="flex gap-2.5">
          {points.map((point, index) => (
            <span
              key={point.weekStart}
              className="min-w-0 flex-1 whitespace-nowrap text-center text-xs"
              style={{ color: 'var(--lh-muted)' }}
            >
              {index % labelEvery === 0
                ? new Date(point.weekStart).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })
                : ''}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Topics({ topics }: { topics: TCompanyTopics }): ReactElement {
  const { t } = useTranslation();

  if (!topics.available) {
    return <Suppressed message={t(`com_ui_leadhr_topics_${topics.reason}`)} />;
  }

  const peak = Math.max(
    ...topics.cohorts.map((cohort) => (cohort.suppressed ? 0 : cohort.conversations)),
    1,
  );

  return (
    <div className="flex flex-col">
      <Muted>{t('com_ui_leadhr_topics_coverage', { pct: topics.coveragePct })}</Muted>
      {topics.cohorts.map((cohort) =>
        cohort.suppressed ? (
          <div key={cohort.key} className="pt-3.5">
            <Suppressed message={t('com_ui_leadhr_cohort_suppressed', { cohort: cohort.key })} />
          </div>
        ) : (
          <div
            key={cohort.key}
            className="mt-3.5 grid grid-cols-1 items-center gap-x-6 gap-y-3 pt-3.5 sm:grid-cols-[minmax(150px,210px)_minmax(0,1fr)]"
            style={{ borderTop: '1px solid var(--lh-rule)' }}
          >
            <div className="flex flex-col gap-1.5">
              <span className="font-semibold">{cohort.key}</span>
              <span className="lh-small" style={{ color: 'var(--lh-muted)' }}>
                {t('com_ui_leadhr_cohort_summary', {
                  conversations: formatCount(cohort.conversations),
                  people: formatCount(cohort.users),
                })}
              </span>
              <Bar width={(cohort.conversations / peak) * 100} />
            </div>
            <div className="flex flex-wrap gap-2">
              {cohort.topics.map((chip) => (
                <span
                  key={chip.topic}
                  className="inline-flex rounded-lg px-3 py-[5px] text-[13px] leading-[1.3]"
                  style={{ background: 'var(--lh-sand)' }}
                >
                  {chip.topic}
                </span>
              ))}
            </div>
          </div>
        ),
      )}
    </div>
  );
}

function Evaluations({ evaluations }: { evaluations: TCompanyEvaluations }): ReactElement {
  const { t } = useTranslation();

  if (!evaluations.available) {
    return <Suppressed message={t(`com_ui_leadhr_evaluations_${evaluations.reason}`)} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div
        className="grid items-start gap-4"
        style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))' }}
      >
        {evaluations.items.map((item) =>
          item.suppressed ? (
            <Suppressed key={item.key} message={t('com_ui_leadhr_question_suppressed')} />
          ) : (
            <div
              key={item.key}
              className="flex flex-col gap-1.5 rounded-lg p-5"
              style={{ background: 'var(--lh-sand)', border: '1px solid var(--lh-line)' }}
            >
              <span>{t(EVALUATION_LABEL[item.key])}</span>
              <span className="lh-stat mt-2.5">{formatPct(item.agreePct)}</span>
              <small className="text-xs leading-normal" style={{ color: 'var(--lh-ink-soft)' }}>
                {t('com_ui_leadhr_eval_note')}
              </small>
            </div>
          ),
        )}
      </div>
      {evaluations.skills.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-sm">{t('com_ui_leadhr_eval_skills')}</span>
          <ul className="flex flex-wrap gap-2">
            {evaluations.skills.map(({ theme, users }) => (
              <li
                key={theme}
                className="rounded-full px-3 py-1 text-xs"
                style={{ background: 'var(--lh-sand)', border: '1px solid var(--lh-line)' }}
              >
                {theme} · {t('com_ui_leadhr_eval_skills_people', { count: users })}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Funnel({ analytics }: { analytics: TCompanyAnalytics }): ReactElement {
  const { t } = useTranslation();
  return (
    <Panel>
      <PanelHead
        title={t('com_ui_leadhr_funnel_title')}
        subtitle={t('com_ui_leadhr_funnel_subtitle')}
      />
      <div className="mb-3.5 mt-[18px] h-px" style={{ background: 'var(--lh-rule)' }} />
      {analytics.funnel.map((step) => (
        <div key={step.key} className="mb-3 flex items-center gap-3.5">
          <span className="w-[110px] flex-none">{t(FUNNEL_LABEL[step.key])}</span>
          <div className="flex-1">
            <Bar width={step.pctOfInvited} />
          </div>
          <span className="w-10 text-right font-semibold">{formatCount(step.count)}</span>
          <span className="w-10 text-right text-xs" style={{ color: 'var(--lh-muted)' }}>
            {formatPct(step.pctOfInvited)}
          </span>
        </div>
      ))}
    </Panel>
  );
}

function Attention({
  analytics,
  onInvite,
  onViewInvitations,
}: {
  analytics: TCompanyAnalytics;
  onInvite: () => void;
  onViewInvitations: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { open, total } = analytics.invitations;
  const { seats, assigned } = analytics.licences;
  const count = (key: string) => analytics.funnel.find((step) => step.key === key)?.count ?? 0;
  const inactive = Math.max(count('activated') - count('active7d'), 0);
  const unassigned = seats === null ? 0 : Math.max(seats - assigned, 0);

  const items = [
    {
      key: 'open',
      show: open > 0,
      icon: 'clock' as const,
      background: 'var(--lh-red)',
      title: t('com_ui_leadhr_attention_open', { n: formatCount(open) }),
      detail: t('com_ui_leadhr_attention_open_detail', {
        pct: total === 0 ? 0 : Math.round((open / total) * 100),
      }),
      action: (
        <LinkAction onClick={onViewInvitations}>{t('com_ui_leadhr_attention_view')}</LinkAction>
      ),
    },
    {
      key: 'inactive',
      show: inactive > 0,
      icon: 'warning' as const,
      background: 'var(--lh-apricot)',
      title: t('com_ui_leadhr_attention_inactive', { n: formatCount(inactive) }),
      detail: t('com_ui_leadhr_attention_inactive_detail'),
      action: null,
    },
    {
      key: 'unassigned',
      show: unassigned > 0,
      icon: 'licences' as const,
      background: 'var(--lh-lgreen)',
      title: t('com_ui_leadhr_attention_unassigned', { n: formatCount(unassigned) }),
      detail: t('com_ui_leadhr_attention_unassigned_detail'),
      action: <LinkAction onClick={onInvite}>{t('com_ui_leadhr_invite_users')}</LinkAction>,
    },
  ].filter((item) => item.show);

  return (
    <Panel tone="sand">
      <PanelHead
        title={t('com_ui_leadhr_attention_title')}
        subtitle={t('com_ui_leadhr_attention_subtitle')}
      />
      <div className="mt-[18px] flex flex-col">
        {items.length === 0 ? <Muted>{t('com_ui_leadhr_attention_none')}</Muted> : null}
        {items.map((item, index) => (
          <div
            key={item.key}
            className={`flex items-center gap-3.5 ${index === 0 ? 'pb-3.5' : 'py-3.5'}`}
            style={index === 0 ? undefined : { borderTop: '1px solid var(--lh-rule)' }}
          >
            <IconTile icon={item.icon} background={item.background} />
            <div className="flex-1 leading-normal">
              {item.title}
              <small className="lh-small block" style={{ color: 'var(--lh-muted)' }}>
                {item.detail}
              </small>
            </div>
            {item.action}
          </div>
        ))}
      </div>
    </Panel>
  );
}
